/**
 * CREW INFO FORM - Formulaire d'édition des informations d'un équipage
 */

import { useEffect, useCallback } from 'react';
import { useForm, FormProvider, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormInput, FormSelect, FormTextarea } from '../ui';
import { Checkbox } from '../ui/checkbox';
import { updateCrewSchema, type UpdateCrewInput, type Crew } from '@/types/crew';
import {
  CREW_FINANCING_TYPE,
  CREW_FINANCING_TYPE_LABELS,
  CREW_STATUS,
  CREW_STATUS_LABELS,
} from '@/types/crew';
import { useVehiclesSelect } from '@/hooks/useVehiclesSelect';
import { useAssociationsSelect } from '@/hooks/useAssociationsSelect';
import { useDiscountsSelect } from '@/hooks/useDiscountsSelect';
import { useProductsSelect } from '@/hooks/useProductsSelect';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { PROBABILITY_SELECT_OPTIONS } from '@/lib/crew-probability-constants';

interface CrewInfoFormProps {
  crew: Crew;
  onUpdate: (data: UpdateCrewInput) => Promise<boolean>;
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmitRef?: (submitFn: () => void) => void;
}

// Options pour le select de statut (exclure 'completed' et 'bpi_payment_pending' car automatiques)
const STATUS_SELECT_OPTIONS = [
  { value: CREW_STATUS.PRE_REGISTERED, label: CREW_STATUS_LABELS[CREW_STATUS.PRE_REGISTERED] },
  { value: CREW_STATUS.BED_INCOMPLETE, label: CREW_STATUS_LABELS[CREW_STATUS.BED_INCOMPLETE] },
  { value: CREW_STATUS.BED_COMPLETE, label: CREW_STATUS_LABELS[CREW_STATUS.BED_COMPLETE] },
  {
    value: CREW_STATUS.CANCELLATION_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.CANCELLATION_COMPLIANT],
  },
  {
    value: CREW_STATUS.CANCELLATION_NON_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.CANCELLATION_NON_COMPLIANT],
  },
  {
    value: CREW_STATUS.POSTPONEMENT_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.POSTPONEMENT_COMPLIANT],
  },
  {
    value: CREW_STATUS.POSTPONEMENT_NON_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.POSTPONEMENT_NON_COMPLIANT],
  },
  { value: CREW_STATUS.COMPLETED, label: CREW_STATUS_LABELS[CREW_STATUS.COMPLETED] },
  { value: CREW_STATUS.ARCHIVED, label: CREW_STATUS_LABELS[CREW_STATUS.ARCHIVED] },
];

export function CrewInfoForm({ crew, onUpdate, onDirtyChange, onSubmitRef }: CrewInfoFormProps) {
  const { vehicles } = useVehiclesSelect();
  const { associations } = useAssociationsSelect();
  const { discounts } = useDiscountsSelect();
  const { products } = useProductsSelect();
  const { canEditCrewSensitiveFields } = usePermissions();
  const { user } = useAuth();
  const canSeeDerogation =
    user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superadmin';

  // Les utilisateurs "user" ne peuvent pas modifier : statut, réduction, numéro d'équipage
  const canEditSensitiveFields = canEditCrewSensitiveFields();

  const form = useForm<UpdateCrewInput>({
    resolver: zodResolver(updateCrewSchema),
    defaultValues: {
      name: crew.name || '',
      crew_number: crew.crew_number || '',
      status: crew.status,
      probability: crew.probability || undefined,
      product_id: crew.product_id || '',
      vehicle_id: crew.vehicle_id || '',
      association_id: crew.association_id || '',
      discount_id: crew.discount_id || '',
      financing_type: crew.financing_type,
      budget: crew.budget || undefined,
      report: crew.report || '',
      has_derogation: crew.has_derogation,
      organizes_pvd: crew.organizes_pvd,
      has_vehicle: crew.has_vehicle ?? false,
      notes: crew.notes || '',
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = form;

  // V2 R4 Phase B : sync vehicle_id → has_vehicle (UI only).
  // Si l'utilisateur sélectionne un véhicule, on force has_vehicle = true.
  // Le décochage manuel reste libre (on ne clear PAS vehicle_id automatiquement),
  // un avertissement visuel est affiché si désaligné (cf. JSX plus bas).
  const watchedVehicleId = useWatch({ control, name: 'vehicle_id' });
  const watchedHasVehicle = useWatch({ control, name: 'has_vehicle' });
  useEffect(() => {
    if (watchedVehicleId && watchedVehicleId !== '' && !watchedHasVehicle) {
      setValue('has_vehicle', true, { shouldDirty: true });
    }
  }, [watchedVehicleId, watchedHasVehicle, setValue]);

  // Fonction de soumission stable
  const onSubmit = useCallback(
    async (data: UpdateCrewInput) => {
      const success = await onUpdate(data);
      if (success) {
        // Reset le formulaire avec les nouvelles valeurs pour remettre isDirty à false
        form.reset(data, { keepValues: true });
      }
    },
    [onUpdate, form]
  );

  // Réinitialiser le formulaire quand le crew change
  useEffect(() => {
    form.reset({
      name: crew.name || '',
      crew_number: crew.crew_number || '',
      status: crew.status,
      probability: crew.probability || undefined,
      product_id: crew.product_id || '',
      vehicle_id: crew.vehicle_id || '',
      association_id: crew.association_id || '',
      discount_id: crew.discount_id || '',
      financing_type: crew.financing_type,
      budget: crew.budget || undefined,
      report: crew.report || '',
      has_derogation: crew.has_derogation,
      organizes_pvd: crew.organizes_pvd,
      has_vehicle: crew.has_vehicle ?? false,
      notes: crew.notes || '',
    });
  }, [crew, form]);

  // Notifier le parent du changement de l'état dirty
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Créer une fonction stable pour la soumission
  const handleFormSubmit = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  // Exposer la fonction de soumission au parent
  useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(handleFormSubmit);
    }
  }, [onSubmitRef, handleFormSubmit]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Notes */}
        <FormTextarea
          control={control}
          name="notes"
          label="Notes"
          placeholder="Tapez vos notes ici"
          rows={6}
        />

        {/* Nom et Statut */}
        <div className="grid grid-cols-2 gap-4">
          <FormInput control={control} name="name" label="Nom d'équipage" />
          <FormSelect
            control={control}
            name="status"
            label="Statut"
            options={STATUS_SELECT_OPTIONS}
            required
            disabled={!canEditSensitiveFields}
          />
        </div>

        {/* Produit */}
        <FormSelect
          control={control}
          name="product_id"
          label="Produit"
          options={[
            { value: '', label: 'Aucun produit' },
            ...products.map((product) => ({ value: product.id, label: product.name })),
          ]}
        />

        {/* Numéro d'équipage, Réduction et Financement */}
        <div className="grid grid-cols-3 gap-4">
          <FormInput
            control={control}
            name="crew_number"
            label="Numéro d'équipage"
            required
            disabled={!canEditSensitiveFields}
          />
          <FormSelect
            control={control}
            name="discount_id"
            label="Réduction"
            options={[
              { value: '', label: 'Aucune' },
              ...discounts.map((d) => ({ value: d.id, label: d.name })),
            ]}
            disabled={!canEditSensitiveFields}
          />
          <FormSelect
            control={control}
            name="financing_type"
            label="Financement"
            options={Object.values(CREW_FINANCING_TYPE).map((type) => ({
              value: type,
              label: CREW_FINANCING_TYPE_LABELS[type],
            }))}
            required
          />
        </div>

        {/* Budget et Motif Annulation/Report */}
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            control={control}
            name="budget"
            label="Budget (€)"
            type="number"
            valueAsNumber
          />
          <FormInput
            control={control}
            name="report"
            label="Motif Annulation ou Report"
            placeholder="Aucun"
            type="text"
          />
        </div>

        {/* Dérogation et Organisation PVD */}
        <div className="grid grid-cols-2 gap-6">
          {canSeeDerogation && (
            <Controller
              control={control}
              name="has_derogation"
              render={({ field }) => (
                <Checkbox
                  checked={field.value || false}
                  onChange={field.onChange}
                  label="Dérogation"
                />
              )}
            />
          )}
          <Controller
            control={control}
            name="organizes_pvd"
            render={({ field }) => (
              <Checkbox
                checked={field.value || false}
                onChange={field.onChange}
                label="Organisation PVD"
              />
            )}
          />
        </div>

        {/* Voiture, Probabilité et Associations */}
        <div className="grid grid-cols-3 gap-4">
          <FormSelect
            control={control}
            name="vehicle_id"
            label="Voiture"
            options={[
              { value: '', label: 'Aucun' },
              ...vehicles.map((v) => ({ value: v.id, label: v.registration })),
            ]}
          />
          <FormSelect
            control={control}
            name="probability"
            label="Probabilité"
            required
            options={PROBABILITY_SELECT_OPTIONS}
          />
          <FormSelect
            control={control}
            name="association_id"
            label="Associations"
            options={[
              { value: '', label: 'Aucune' },
              ...associations.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        </div>

        {/* V2 R4 Phase B : case "Possède un véhicule" indépendante de vehicle_id */}
        <div className="space-y-2">
          <Controller
            control={control}
            name="has_vehicle"
            render={({ field }) => (
              <Checkbox
                checked={field.value || false}
                onChange={field.onChange}
                label="Possède un véhicule"
              />
            )}
          />
          {/* Avertissement si décochée alors qu'un véhicule est sélectionné */}
          {watchedVehicleId && watchedVehicleId !== '' && !watchedHasVehicle && (
            <p className="text-xs text-orange-600">
              Un véhicule est sélectionné mais la case « Possède un véhicule » est décochée.
            </p>
          )}
        </div>
      </form>
    </FormProvider>
  );
}
